import { ActionFunctionArgs, LoaderFunctionArgs, json, redirect } from "@remix-run/node";
import type { MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { validateSupabaseConfig } from '~/lib/supabase-config';
import { createSupabaseServerClient } from '~/lib/supabase-utils';
import { checkOnboardingStatus } from '~/lib/onboarding-utils';
import { appendWaitlistSignup } from '~/lib/waitlist.server';
import { Landing as CaseStudyLanding } from '~/deck2/landing/Landing';
import { LandingPage } from '~/landing_page/components/LandingPage/LandingPage';
import { LandingPageV2 } from '~/landing_page/v2/LandingPageV2';
import { WaitlistLanding } from '~/landing_page/components/WaitlistLanding';
import type { WaitlistActionData } from '~/landing_page/components/WaitlistLanding';
import { links as caseStudyLinks } from './cases.landing';

// The public face of `/` is the case study landing (`~/deck2/landing`, also at /cases/landing). The earlier
// pages are kept to reuse and stay reachable to preview: ?landing-2 is the dark landing that was `/` until
// the case study launch, ?landing=1 the light landing and ?waitlist=1 the waitlist page.
type IndexLoaderData = { mode: 'cases' | 'landing' | 'landing2' | 'waitlist' };

export const links = caseStudyLinks;

export async function loader({ request }: LoaderFunctionArgs) {
  const response = new Response();

  // Config broken → fall back to the public landing rather than bounce to a broken sign-in.
  if (!validateSupabaseConfig()) {
    console.error("Supabase configuration invalid in _index.tsx.");
    return json<IndexLoaderData>({ mode: 'cases' });
  }

  // ?landing=1 previews the full marketing landing (even signed in); ?waitlist=1 previews the
  // waitlist page while signed in, without having to sign out first.
  const url = new URL(request.url);
  if (url.searchParams.has('landing-2')) {
    return json<IndexLoaderData>(
      { mode: 'landing2' },
      { headers: response.headers },
    );
  }
  if (url.searchParams.has('landing')) {
    return json<IndexLoaderData>(
      { mode: 'landing' },
      { headers: response.headers },
    );
  }
  if (url.searchParams.has('waitlist')) {
    return json<IndexLoaderData>(
      { mode: 'waitlist' },
      { headers: response.headers },
    );
  }

  const supabase = createSupabaseServerClient(request, response);
  const { data: { session } } = await supabase.auth.getSession();

  // ONE domain (usedeck.ai): signed in → straight to the dashboard (or onboarding); signed out → the
  // public landing.
  // Redirect on the server rather than returning a "redirecting" page that bounces from an effect.
  // The old client bounce had to paint something first, and what it painted was the magic-link
  // verifying screen — so every signed-in visit to `/`, including opening it in a new tab, flashed
  // "Verifying your magic link" at someone who was already signed in. checkOnboardingStatus only
  // ever returns a same-origin path, so there is nothing a 302 cannot carry.
  if (session) {
    const redirectTo = await checkOnboardingStatus(request);
    return redirect(redirectTo, { headers: response.headers });
  }

  return json<IndexLoaderData>(
    { mode: 'cases' },
    { headers: response.headers },
  );
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData();

  // A filled honeypot is a bot: report success and write nothing.
  const website = String(form.get('website') ?? '');
  if (website.trim() !== '') {
    return json<WaitlistActionData>({ ok: true });
  }

  const email = String(form.get('email') ?? '').trim().toLowerCase();
  // The input is type=email, so a real browser never submits past this.
  if (!email || email.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return json<WaitlistActionData>(
      { ok: false, error: 'That email does not look right.' },
      { status: 400 },
    );
  }

  try {
    await appendWaitlistSignup(email, 'landing');
  } catch (e) {
    console.error('Waitlist signup failed:', e instanceof Error ? e.message : e);
    return json<WaitlistActionData>(
      { ok: false, error: 'Something went wrong. Please try again.' },
      { status: 500 },
    );
  }

  return json<WaitlistActionData>({ ok: true });
}

// Absolute on purpose: link previews are fetched by crawlers, which need a full URL, and the public
// address is the right one whichever build (local, staging, production) served the page.
const SITE_URL = 'https://usedeck.ai';
const TITLE = 'Deck — The Storytelling Agent for Business';
const DESCRIPTION = 'Deck collects your work where it lives and transforms it into clear, compelling stories.';
const OG_IMAGE = `${SITE_URL}/landing/og/deck-og-storytelling.jpg`;

const CASE_TITLE = 'Deck · Your next case study gets you hired';
const CASE_DESCRIPTION = 'Pick what it is about, bring your work, and Deck turns it into a case study you can present.';
const CASE_OG_IMAGE = `${SITE_URL}/landing/og/deck-og-case-study.jpg`;
const CASE_OG_ALT = 'Your next case study gets you hired, over case studies made in Deck for Headspace, Stripe and Wise.';

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: data?.mode === 'cases' ? CASE_TITLE : TITLE },
  { name: 'description', content: data?.mode === 'cases' ? CASE_DESCRIPTION : DESCRIPTION },
  { tagName: 'link', rel: 'canonical', href: SITE_URL },
  { property: 'og:type', content: 'website' },
  { property: 'og:site_name', content: 'Deck' },
  { property: 'og:url', content: SITE_URL },
  { property: 'og:title', content: data?.mode === 'cases' ? CASE_TITLE : TITLE },
  { property: 'og:description', content: data?.mode === 'cases' ? CASE_DESCRIPTION : DESCRIPTION },
  { property: 'og:image', content: data?.mode === 'cases' ? CASE_OG_IMAGE : OG_IMAGE },
  { property: 'og:image:width', content: '1200' },
  { property: 'og:image:height', content: '630' },
  {
    property: 'og:image:alt',
    content: data?.mode === 'cases' ? CASE_OG_ALT : 'A wall of decks made in Deck, around the line: Storytelling is everybody’s job.',
  },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: data?.mode === 'cases' ? CASE_TITLE : TITLE },
  { name: 'twitter:description', content: data?.mode === 'cases' ? CASE_DESCRIPTION : DESCRIPTION },
  { name: 'twitter:image', content: data?.mode === 'cases' ? CASE_OG_IMAGE : OG_IMAGE },
];

export default function Index() {
  const { mode } = useLoaderData<typeof loader>();
  if (mode === 'cases') return <CaseStudyLanding />;
  if (mode === 'landing2') return <LandingPageV2 />;
  return mode === 'landing' ? <LandingPage /> : <WaitlistLanding />;
}
